import React from 'react';
import { Typography, Card, Row, Col, Spin, Input } from 'antd';
import { gql, useQuery } from '@apollo/client';
import './Profile.css';

const { Title, Text } = Typography;

// GraphQL query to get user profile information including contacts
const GET_USER_PROFILE = gql`
  query GetUserProfile($userId: Float!) {
    user(id: $userId) {
      id
      firstName
      lastName
      email
      phoneNumbersList {
        number
        type
      }
      cellPhone
      address {
        addressLine1
        addressLine2
        city
        state
        zip
      }
      contacts {
        id
        firstName
        lastName
        email
        type
        cellPhone
        homePhone
        primaryPhone
        isPoa
        address {
          addressLine1
          addressLine2
          city
          state
          zip
        }
      }
      accountManager {
        id
        firstName
        lastName
        email
        cellPhone
      }
    }
  }
`;

// Fallback data for account manager if not available from API
const fallbackAccountManager = {
  firstName: "Not Assigned",
  lastName: "",
  email: "N/A",
  phoneNumber: "N/A"
};

interface PhoneNumber {
  number: string;
  type: string;
}
interface Contact {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  type: string;
  cellPhone?: string;
  homePhone?: string;
  primaryPhone?: string;
  isPoa?: boolean;
  address?: {
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zip: string;
  };
}

interface UserProfileData {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumbersList: PhoneNumber[];
    cellPhone: string;
    address: {
      addressLine1: string;
      addressLine2: string;
      city: string;
      state: string;
      zip: string;
    };
    contacts?: Contact[];
    accountManager?: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      cellPhone: string;
    };
  };
}

interface AccountManager {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
}

interface UserProfileVars {
  userId: number;
}

const Profile: React.FC = () => {
  const userId = Number(localStorage.getItem('userId'));
  
  const { loading, error, data: userData } = useQuery<UserProfileData, UserProfileVars>(
    GET_USER_PROFILE,
    {
      variables: { userId },
    }
  );


  if (loading) return <Spin size="large" />;
  if (error) return <Typography.Text type="danger">Error loading profile: {error.message}</Typography.Text>;
  
  const user = userData?.user;
  
  // Use real account manager data from the API, fallback if not available
  const accountManager: AccountManager = user?.accountManager ? {
    firstName: user.accountManager.firstName,
    lastName: user.accountManager.lastName,
    email: user.accountManager.email,
    phoneNumber: user.accountManager.cellPhone || 'N/A'
  } : fallbackAccountManager;
  
  if (!user) return <Typography.Text type="warning">No profile information available.</Typography.Text>;
  
  // Find home phone from phoneNumbersList (look for landline type)
  const homePhone = user.phoneNumbersList?.find(phone => phone.type === 'landline')?.number || 'N/A';

  return (
    <div className="profile-container">
      <Title level={2}>Profile</Title>
      
      <Card className="profile-card">
        <Title level={3}>Client Information</Title>
        
        <Row gutter={[24, 16]}>
          <Col span={12}>
            <div className="profile-field-row">
              <Text strong>First Name:</Text>
              <Input value={user.firstName || ''} readOnly />
            </div>
          </Col>
          <Col span={12}>
            <div className="profile-field-row">
              <Text strong>Address Line 1:</Text>
              <Input value={user.address?.addressLine1 || ''} readOnly />
            </div>
          </Col>
        </Row>
        
        <Row gutter={[24, 16]}>
          <Col span={12}>
            <div className="profile-field-row">
              <Text strong>Last Name:</Text>
              <Input value={user.lastName || ''} readOnly />
            </div>
          </Col>
          <Col span={12}>
            <div className="profile-field-row">
              <Text strong>Address Line 2:</Text>
              <Input value={user.address?.addressLine2 || ''} readOnly />
            </div>
          </Col>
        </Row>
        
        <Row gutter={[24, 16]}>
          <Col span={12}>
            <div className="profile-field-row">
              <Text strong>Email Address:</Text>
              <Input value={user.email || ''} readOnly />
            </div>
          </Col>
          <Col span={12}>
            <div className="profile-field-row">
              <Text strong>City:</Text>
              <Input value={user.address?.city || ''} readOnly />
            </div>
          </Col>
        </Row>
        
        <Row gutter={[24, 16]}>
          <Col span={12}>
            <div className="profile-field-row">
              <Text strong>Home Phone:</Text>
              <Input value={homePhone} readOnly />
            </div>
          </Col>
          <Col span={12}>
            <div className="profile-field-row">
              <Text strong>State:</Text>
              <Input value={user.address?.state || ''} readOnly />
            </div>
          </Col>
        </Row>
        
        <Row gutter={[24, 16]}>
          <Col span={12}>
            <div className="profile-field-row">
              <Text strong>Cell Phone:</Text>
              <Input value={user.cellPhone || ''} readOnly />
            </div>
          </Col>
          <Col span={12}>
            <div className="profile-field-row">
              <Text strong>Zip Code:</Text>
              <Input value={user.address?.zip || ''} readOnly />
            </div>
          </Col>
        </Row>
      </Card>
      
      {/* Trusted Contacts Section - Only show if contacts exist */}
      {user.contacts && user.contacts.length > 0 && (
        <>
          <div style={{ height: 24 }} />
          
          <Card className="profile-card">
            <Title level={3}>Trusted Contacts</Title>
            
            {user.contacts.map((contact, index) => (
              <div key={contact.id} style={{ marginBottom: index < user.contacts!.length - 1 ? 24 : 0 }}>
                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <div className="profile-field-row">
                      <Text strong>First Name:</Text>
                      <Input value={contact.firstName || ''} readOnly />
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="profile-field-row">
                      <Text strong>Address Line 1:</Text>
                      <Input value={contact.address?.addressLine1 || ''} readOnly />
                    </div>
                  </Col>
                </Row>
                
                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <div className="profile-field-row">
                      <Text strong>Last Name:</Text>
                      <Input value={contact.lastName || ''} readOnly />
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="profile-field-row">
                      <Text strong>Address Line 2:</Text>
                      <Input value={contact.address?.addressLine2 || ''} readOnly />
                    </div>
                  </Col>
                </Row>
                
                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <div className="profile-field-row">
                      <Text strong>Email:</Text>
                      <Input value={contact.email || ''} readOnly />
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="profile-field-row">
                      <Text strong>City:</Text>
                      <Input value={contact.address?.city || ''} readOnly />
                    </div>
                  </Col>
                </Row>
                
                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <div className="profile-field-row">
                      <Text strong>Home Phone:</Text>
                      <Input value={contact.homePhone && contact.homePhone !== 'N/A' ? contact.homePhone : ''} readOnly />
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="profile-field-row">
                      <Text strong>State:</Text>
                      <Input value={contact.address?.state || ''} readOnly />
                    </div>
                  </Col>
                </Row>
                
                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <div className="profile-field-row">
                      <Text strong>Cell Phone:</Text>
                      <Input value={contact.cellPhone || ''} readOnly />
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="profile-field-row">
                      <Text strong>ZIP Code:</Text>
                      <Input value={contact.address?.zip || ''} readOnly />
                    </div>
                  </Col>
                </Row>
                
                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <div className="profile-field-row">
                      <Text strong>Contact Type:</Text>
                      <Input value={contact.type || ''} readOnly />
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="profile-field-row">
                      <Text strong>Power of Attorney:</Text>
                      <Input value={contact.isPoa ? 'Yes' : 'No'} readOnly />
                    </div>
                  </Col>
                </Row>
                
                {index < user.contacts!.length - 1 && (
                  <div style={{ height: 16, borderBottom: '1px solid #f0f0f0', marginTop: 16 }} />
                )}
              </div>
            ))}
          </Card>
        </>
      )}
      
      <div style={{ height: 24 }} />
      
      <Card className="profile-card">
        <Title level={3}>Account Manager Information</Title>
        
        <Row gutter={[24, 16]}>
          <Col span={12}>
            <div className="profile-field-row">
              <Text strong>First Name:</Text>
              <Input value={accountManager.firstName || ''} readOnly />
            </div>
          </Col>
        </Row>
        
        <Row gutter={[24, 16]}>
          <Col span={12}>
            <div className="profile-field-row">
              <Text strong>Last Name:</Text>
              <Input value={accountManager.lastName || ''} readOnly />
            </div>
          </Col>
        </Row>
        
        <Row gutter={[24, 16]}>
          <Col span={12}>
            <div className="profile-field-row">
              <Text strong>Email Address:</Text>
              <Input value={accountManager.email || ''} readOnly />
            </div>
          </Col>
        </Row>
        
        <Row gutter={[24, 16]}>
          <Col span={12}>
            <div className="profile-field-row">
              <Text strong>Work Phone:</Text>
              <Input value={accountManager.phoneNumber || ''} readOnly />
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default Profile;
